'use client';

import { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

/** Where a slide-over may paint, given whatever chrome hosts the viewer. */
export type SlideOverChrome = {
  /** Positions the panel layer. Must clear any chrome the host paints on top. */
  band: string;
  /** Stacking order shared by the backdrop and the panel layer. */
  layer: string;
};

/** The standalone viewer owns the viewport, so a slide-over covers all of it. */
const STANDALONE_CHROME: SlideOverChrome = { band: 'fixed inset-0', layer: 'z-30' };

/**
 * Citation tables render inside chat, whose stacking context sits under the
 * work-product sub-header (`#work-product-nav-bar`, "Assistant" + case name,
 * `z-40`). A `fixed` layer in that tree cannot paint over that bar, so the
 * slide-over is portaled to `document.body` and starts where `#main-content`
 * starts for chat with a case (`top-[150px] sm:top-[147px]` in `app/main.tsx`).
 *
 * `z-[10051]` is the evidence-locker overlay band: below the primary nav
 * (`z-[99999]`), above the sub-header and status strip (`z-40`).
 */
export const APP_SHELL_SLIDE_OVER_CHROME: SlideOverChrome = {
  band: 'fixed inset-x-0 bottom-0 top-[150px] sm:top-[147px]',
  layer: 'z-[10051]',
};

const SlideOverChromeContext = createContext<SlideOverChrome>(STANDALONE_CHROME);

export const SlideOverChromeProvider = SlideOverChromeContext.Provider;

/**
 * Shell for a right-hand slide-over: a dismissing backdrop plus the layer the
 * panel sits in, both placed for the host chrome. Always portaled so host
 * stacking (chat `z-1`, work-product `z-40`) cannot bury the panel.
 */
export function SlideOverLayer({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { band, layer } = useContext(SlideOverChromeContext);

  const node = (
    <>
      <div className={`${band} ${layer} bg-black/50`} onClick={onClose} />
      <div
        className={`${band} ${layer} pointer-events-none flex justify-end [&>*]:pointer-events-auto`}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}
